import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { createMonster } from "@/lib/game/monster.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/game/Panel";
import { Pill, TypeBadge } from "@/components/game/Badges";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { tServerError } from "@/lib/i18n/server-error";
import type { CreatureType } from "@/lib/game/types";

export const Route = createFileRoute("/_app/game/onboarding")({
  component: Onboarding,
});

// Reihenfolge wie bisher. Die Farbe kommt aus `TypeBadge` — die fünf Typfarben
// sind semantisch und werden hier nicht noch einmal von Hand gemischt.
const ELEMENTS: readonly CreatureType[] = ["neon", "void", "data", "plasma", "glitch"];

/** Kachel des Element-Wählers: gewählt = Orange-Outline + voller Hartschatten. */
function elementTile(selected: boolean) {
  return cn(
    "flex min-h-11 items-center justify-center rounded-md border-2 px-2 py-2",
    "cursor-pointer transition-[transform,box-shadow,filter] duration-100",
    "hover:brightness-110 hover:-translate-y-px active:translate-y-[5px] active:shadow-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    selected
      ? "border-primary bg-secondary shadow-hard"
      : "border-ink bg-card shadow-hard-sm",
  );
}

function Onboarding() {
  const { t } = useTranslation("game");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const create = useServerFn(createMonster);
  const [vibe, setVibe] = useState("");
  const [element, setElement] = useState<CreatureType | "random">("random");

  const mut = useMutation({
    mutationFn: () => create({ data: { vibe: vibe || undefined, element } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-monster"] });
      // Battle-v2-Caches mit invalidieren: falls /game/battle in dieser Session
      // schon ohne Monster besucht wurde, hinge sonst "Kein aktives Xenomon"
      // im 60s-staleTime-Cache.
      qc.invalidateQueries({ queryKey: ["battle-v2"] });
      // Direkt in den v2-Kampf — /game/home ist der alte Idle-Screen (Battle v1,
      // Roadmap Stufe 5). getPlayerProgress seedet Zone 1 / Stage 1 automatisch.
      navigate({ to: "/game/battle" });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  return (
    <div className="relative min-h-screen px-4 py-10">
      <div className="halftone pointer-events-none absolute inset-0 opacity-70" aria-hidden />
      <div className="relative z-10 mx-auto w-full max-w-2xl">
        <div className="text-center">
          <h1 className="text-[2rem] leading-tight [text-shadow:4px_4px_0_var(--ink)] sm:text-4xl">
            {t("onboarding.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-[52ch] text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("onboarding.subtitle")}
          </p>
        </div>

        <Panel grid className="mt-8" contentClassName="p-5 sm:p-7">
          <div>
            <Label className="mb-2.5 block font-display text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              {t("onboarding.labelElement")}
            </Label>
            <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
              <button
                type="button"
                aria-pressed={element === "random"}
                onClick={() => setElement("random")}
                className={elementTile(element === "random")}
              >
                <Pill
                  style={{ background: "var(--secondary)", color: "var(--foreground)" }}
                  className="[overflow-wrap:anywhere]"
                >
                  {t("onboarding.random")}
                </Pill>
              </button>
              {ELEMENTS.map((typ) => (
                <button
                  key={typ}
                  type="button"
                  aria-pressed={element === typ}
                  onClick={() => setElement(typ)}
                  className={elementTile(element === typ)}
                >
                  <TypeBadge type={typ} />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <Label
              htmlFor="vibe"
              className="mb-2 block font-display text-[10px] uppercase tracking-[0.12em] text-muted-foreground"
            >
              {t("onboarding.labelVibe")}
            </Label>
            <Input
              id="vibe"
              placeholder={t("onboarding.vibePlaceholder")}
              value={vibe}
              onChange={(e) => setVibe(e.target.value)}
              maxLength={80}
            />
          </div>

          {/*
            Der eine orange Knopf dieser Ansicht. `whitespace-normal` ist Pflicht:
            "Erschaffe Kreatur… (kann ~20s dauern)" passt auf 390 px nicht in eine
            Zeile, und Buttons sind per Default nowrap.
          */}
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            variant="gradient"
            size="lg"
            className="mt-7 min-h-14 w-full whitespace-normal px-5 text-center text-base leading-tight sm:text-lg"
          >
            {mut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("onboarding.btnCreating")}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" strokeWidth={2.5} />
                {t("onboarding.btnCreate")}
              </>
            )}
          </Button>
        </Panel>
      </div>
    </div>
  );
}
